// Types concerned with modeler state

import _ from "lodash";
import { deleteIdx, updateIdx } from "../workbenchLogic";
import { Option } from "../types";

export type RoleInheritanceConfiguration = {
  parentRole: string;
  thisRole: string;
};
export type RoleInheritance =
  | { kind: "simple" }
  | {
      kind: "configured";
      configuration: RoleInheritanceConfiguration[];
    };

/** rather than storing a relation name, for now we'll just default to naming
 *  relations the snake-cased version of the related resource name */
export type Relation = {
  resource: string;
  /** invariant: kind is "simple" iff isGrantedAccessTo is false */
  roleInheritance: RoleInheritance;
};

export type Resource = {
  name: string;
  /** invariant: if this is false, then canShareWithOtherUsers is null */
  isGrantedAccessTo: boolean;
  relations: Relation[];
  roles: string[];
  permissions: string[];
  rolePermissionImplications: {
    role: string;
    permission: string;
  }[];
  sharing: Sharing;
};

export type Sharing = {
  /** list of roles that can be shared to other users. Invariant: if this is non-null, isGrantedAccessTo is true */
  canShareWithOtherUsers: Option<string[]>;
  canShareWithParent: SharingWithParentConfiguration[];
  /** list of permissions users get if the resource is public */
  canSharePublic: Option<string[]>;
};

export type SharingWithParentConfiguration = {
  parent: string;
  configuration: RoleInheritanceConfiguration[];
};

// All the state.
export type StateWithMetadata = {
  // The ordered list of users who have shared this model.
  sharedBy: string[];
  // The state schema version, provided in case we need to make breaking
  // changes to the schema and create a state transformation function for
  // translating from schema A to A'.
  stateVersion: string;
  state: State;
};

export type State = {
  resources: Resource[];
};

export function defaultRoleInheritanceConfiguration(
  state: State,
  thisResourceName: string,
  parentResourceName: string
): RoleInheritanceConfiguration[] {
  const thisResource = state.resources.find(
    (resource) => resource.name === thisResourceName
  );
  const parentResource = state.resources.find(
    (resource) => resource.name === parentResourceName
  );
  if (!thisResource || !parentResource) {
    throw new Error(
      `Could not find resource ${thisResourceName} or ${parentResourceName}`
    );
  }
  return parentResource.roles.flatMap((parentRole) =>
    thisResource.roles.includes(parentRole)
      ? [{ parentRole, thisRole: parentRole }]
      : []
  );
}
export function updateResourceIsGrantedAccessTo(
  state: State,
  resourceName: string,
  isGrantedAccessTo: boolean
): State {
  const resourceIndex = state.resources.findIndex(
    (resource) => resource.name === resourceName
  );
  const resource = state.resources[resourceIndex];
  const relations = resource.relations.map((relation) => {
    const roleInheritance: RoleInheritance = isGrantedAccessTo
      ? {
          kind: "configured",
          configuration: defaultRoleInheritanceConfiguration(
            state,
            resourceName,
            relation.resource
          ),
        }
      : { kind: "simple" };
    return { ...relation, roleInheritance };
  });
  const sharing = isGrantedAccessTo
    ? resource.sharing
    : { ...resource.sharing, canShareWithOtherUsers: null };
  const resources = updateIdx(state.resources, resourceIndex, {
    ...resource,
    isGrantedAccessTo,
    relations,
    sharing,
  });
  return { ...state, resources };
}

export type GrantedParent = {
  ancestorResource: string;
  ancestorRoles: string[];
  directRelation: Relation;
};
/** Travels up the hierarchy to get the resource(s) that this one
 * inherits access from, and returns:
 * - the ancestor that access is granted to (user-facing)
 * - the roles that can be granted on the ancestor (user-facing)
 * - the relation on this resource that this comes from (for internal state management)
 */
export function grantedParents(
  state: State,
  resourceName: string,
  visited?: Set<string>
): GrantedParent[] {
  const seen = visited ?? new Set();
  if (seen.has(resourceName)) {
    return [];
  } else {
    seen.add(resourceName);
  }

  const resource = state.resources.find(
    (resource) => resource.name === resourceName
  );
  if (!resource) {
    throw new Error(`No resource named ${resourceName}`);
  }
  return _.uniqWith(
    resource.relations.flatMap((directRelation) => {
      const relatedResource = state.resources.find(
        (res) => res.name === directRelation.resource
      );
      if (!relatedResource) {
        throw new Error(`No resource named ${directRelation.resource}`);
      }
      if (relatedResource.isGrantedAccessTo) {
        return [
          {
            ancestorResource: relatedResource.name,
            ancestorRoles: relatedResource.roles,
            directRelation,
          },
        ];
      } else {
        return grantedParents(state, directRelation.resource, seen).map(
          (grantedParent) => ({
            ...grantedParent,
            directRelation,
          })
        );
      }
    }),
    _.isEqual
  );
}

export function editRole(
  state: State,
  resourceName: string,
  prevRoleName: string,
  newRoleName: string
): State {
  function editThisResource(resource: Resource): Resource {
    const roles = _.uniq(
      resource.roles.map((role) => (role === prevRoleName ? newRoleName : role))
    );
    const rolePermissionImplications = resource.rolePermissionImplications.map(
      (implication) => ({
        ...implication,
        role:
          implication.role === prevRoleName ? newRoleName : implication.role,
      })
    );
    const relations = resource.relations.map((relation) => {
      if (relation.roleInheritance.kind === "simple") {
        throw new Error("this is far too simple");
      }
      const configuration = relation.roleInheritance.configuration.map(
        (config) => ({
          ...config,
          thisRole:
            config.thisRole === prevRoleName ? newRoleName : config.thisRole,
        })
      );
      const roleInheritance = { ...relation.roleInheritance, configuration };
      return { ...relation, roleInheritance };
    });
    const sharing: Sharing = {
      ...resource.sharing,
      canShareWithParent: resource.sharing.canShareWithParent.map(
        (shareWithParent) => ({
          ...shareWithParent,
          configuration: shareWithParent.configuration.map((implication) =>
            implication.thisRole === prevRoleName
              ? { ...implication, thisRole: newRoleName }
              : implication
          ),
        })
      ),
      canShareWithOtherUsers: resource.sharing.canShareWithOtherUsers
        ? resource.sharing.canShareWithOtherUsers.map((role) =>
            role === prevRoleName ? newRoleName : role
          )
        : null,
    };
    return {
      ...resource,
      roles,
      rolePermissionImplications,
      relations,
      sharing,
    };
  }
  function editGrantedDescendant(resource: Resource): Resource {
    const relations = resource.relations.map((relation) => {
      if (relation.roleInheritance.kind === "simple") {
        throw new Error("this is far too simple");
      }
      if (visited.has(relation.resource)) {
        const configuration = relation.roleInheritance.configuration.map(
          (config) => ({
            ...config,
            parentRole:
              config.parentRole === prevRoleName
                ? newRoleName
                : config.parentRole,
          })
        );
        const roleInheritance = { ...relation.roleInheritance, configuration };
        return { ...relation, roleInheritance };
      } else {
        return relation;
      }
    });
    const sharing: Sharing = {
      ...resource.sharing,
      canShareWithParent: resource.sharing.canShareWithParent.map(
        (shareWithParent) => ({
          ...shareWithParent,
          configuration: shareWithParent.configuration.map((implication) =>
            implication.parentRole === prevRoleName
              ? { ...implication, parentRole: newRoleName }
              : implication
          ),
        })
      ),
    };
    return { ...resource, relations, sharing };
  }
  function editNonGrantedDescendant(resource: Resource): Resource {
    const roles = _.uniq(
      grantedParents(state, resource.name).flatMap((gp) =>
        gp.ancestorResource === resourceName
          ? gp.ancestorRoles.map((role) =>
              role === prevRoleName ? newRoleName : role
            )
          : gp.ancestorRoles
      )
    );
    const rolePermissionImplications = resource.rolePermissionImplications.map(
      (implication) => ({
        ...implication,
        role:
          implication.role === prevRoleName ? newRoleName : implication.role,
      })
    );
    const sharing: Sharing = {
      ...resource.sharing,
      canShareWithParent: resource.sharing.canShareWithParent.map(
        (shareWithParent) => ({
          ...shareWithParent,
          configuration: shareWithParent.configuration.map((implication) =>
            implication.parentRole === prevRoleName
              ? { ...implication, parentRole: newRoleName }
              : implication
          ),
        })
      ),
    };
    return { ...resource, roles, rolePermissionImplications, sharing };
  }
  const visited = new Set<string>();
  function dfs(state: State, resourceNode: string): State {
    if (visited.has(resourceNode)) {
      return state;
    } else {
      visited.add(resourceNode);
    }
    const resourceIndex = state.resources.findIndex(
      (res) => res.name === resourceNode
    );
    const resource = state.resources[resourceIndex];
    const newResource =
      resource.name === resourceName
        ? editThisResource(resource)
        : resource.isGrantedAccessTo
        ? editGrantedDescendant(resource)
        : editNonGrantedDescendant(resource);
    const resources = updateIdx(state.resources, resourceIndex, newResource);
    const newState = { ...state, resources };
    const children = state.resources
      .filter((res) =>
        res.relations.some((rel) => rel.resource === resourceNode)
      )
      .map((child) => child.name);
    return children.reduce(dfs, newState);
  }
  return dfs(state, resourceName);
}

export function addRole(
  state: State,
  resourceName: string,
  role: string
): State {
  const visited = new Set<string>();
  function dfs(state: State, resourceNode: string): State {
    if (visited.has(resourceNode)) {
      return state;
    }
    const resourceIndex = state.resources.findIndex(
      (res) => res.name === resourceNode
    );
    const resource = state.resources[resourceIndex];
    if (resource.isGrantedAccessTo && resource.name !== resourceName) {
      return state;
    }
    const roles = _.uniq([...resource.roles, role]);
    const newResource = { ...resource, roles };
    const resources = updateIdx(state.resources, resourceIndex, newResource);
    const newState = { ...state, resources };
    const children = state.resources
      .filter((res) =>
        res.relations.some((rel) => rel.resource === resourceNode)
      )
      .map((child) => child.name);
    return children.reduce(dfs, newState);
  }
  return dfs(state, resourceName);
}

export function deleteRole(
  state: State,
  resourceName: string,
  role: string
): State {
  function deleteFromThisResource(resource: Resource): Resource {
    const roles = resource.roles.filter((r) => r !== role);
    const rolePermissionImplications =
      resource.rolePermissionImplications.filter(
        (implication) => implication.role !== role
      );
    const relations = resource.relations.map((relation) => {
      if (relation.roleInheritance.kind === "simple") {
        throw new Error("this is far too simple");
      }
      const configuration = relation.roleInheritance.configuration.filter(
        (config) => config.thisRole !== role
      );
      const roleInheritance = { ...relation.roleInheritance, configuration };
      return { ...relation, roleInheritance };
    });
    const sharing = {
      ...resource.sharing,
      canShareWithParent: resource.sharing.canShareWithParent.map(
        (shareWithParent) => ({
          ...shareWithParent,
          configuration: shareWithParent.configuration.filter(
            (implication) => implication.thisRole !== role
          ),
        })
      ),
      canShareWithOtherUsers: resource.sharing.canShareWithOtherUsers
        ? resource.sharing.canShareWithOtherUsers.filter((r) => r !== role)
        : null,
    };
    return {
      ...resource,
      roles,
      rolePermissionImplications,
      relations,
      sharing,
    };
  }
  function deleteFromGrantedDescendant(resource: Resource): Resource {
    const relations = resource.relations.map((relation) => {
      if (relation.roleInheritance.kind === "simple") {
        throw new Error("this is far too simple");
      }
      if (visited.has(relation.resource)) {
        const configuration = relation.roleInheritance.configuration.filter(
          (config) => config.parentRole !== role
        );
        const roleInheritance = { ...relation.roleInheritance, configuration };
        return { ...relation, roleInheritance };
      } else {
        return relation;
      }
    });
    const sharing = {
      ...resource.sharing,
      canShareWithParent: resource.sharing.canShareWithParent.map(
        (shareWithParent) => ({
          ...shareWithParent,
          configuration: shareWithParent.configuration.filter(
            (implication) => implication.parentRole !== role
          ),
        })
      ),
    };
    return { ...resource, relations, sharing };
  }
  function deleteFromNonGrantedDescendant(resource: Resource): Resource {
    const roles = _.uniq(
      grantedParents(state, resource.name).flatMap((gp) =>
        gp.ancestorResource === resourceName
          ? gp.ancestorRoles.filter((r) => r !== role)
          : gp.ancestorRoles
      )
    );
    const rolePermissionImplications =
      resource.rolePermissionImplications.filter(
        (implication) => implication.role !== role
      );
    const sharing = {
      ...resource.sharing,
      canShareWithParent: resource.sharing.canShareWithParent.map(
        (shareWithParent) => ({
          ...shareWithParent,
          configuration: shareWithParent.configuration.filter(
            (implication) => implication.parentRole !== role
          ),
        })
      ),
    };
    return { ...resource, roles, rolePermissionImplications, sharing };
  }
  const visited = new Set<string>();
  function dfs(state: State, resourceNode: string): State {
    if (visited.has(resourceNode)) {
      return state;
    }
    const resourceIndex = state.resources.findIndex(
      (res) => res.name === resourceNode
    );
    const resource = state.resources[resourceIndex];
    const newResource =
      resource.name === resourceName
        ? deleteFromThisResource(resource)
        : resource.isGrantedAccessTo
        ? deleteFromGrantedDescendant(resource)
        : deleteFromNonGrantedDescendant(resource);
    const resources = updateIdx(state.resources, resourceIndex, newResource);
    const newState = { ...state, resources };
    const children = state.resources
      .filter((res) =>
        res.relations.some((rel) => rel.resource === resourceNode)
      )
      .map((child) => child.name);
    return children.reduce(dfs, newState);
  }
  return dfs(state, resourceName);
}

export function editPermission(
  state: State,
  resourceName: string,
  prevPermissionName: string,
  newPermissionName: string
): State {
  const resourceIndex = state.resources.findIndex(
    (resource) => resource.name === resourceName
  );
  const resource = state.resources[resourceIndex];
  const permissions = _.uniq(
    resource.permissions.map((permission) =>
      permission === prevPermissionName ? newPermissionName : permission
    )
  );
  const rolePermissionImplications = resource.rolePermissionImplications.map(
    (implication) => ({
      ...implication,
      permission:
        implication.permission === prevPermissionName
          ? newPermissionName
          : implication.permission,
    })
  );
  const sharing = {
    ...resource.sharing,
    canSharePublic: resource.sharing.canSharePublic
      ? resource.sharing.canSharePublic.map((p) =>
          p === prevPermissionName ? newPermissionName : p
        )
      : null,
  };
  const newResource = {
    ...resource,
    permissions,
    rolePermissionImplications,
    sharing,
  };
  const resources = updateIdx(state.resources, resourceIndex, newResource);
  return { ...state, resources };
}

export function deletePermission(
  state: State,
  resourceName: string,
  permission: string
): State {
  const resourceIndex = state.resources.findIndex(
    (resource) => resource.name === resourceName
  );
  const resource = state.resources[resourceIndex];
  const permissions = resource.permissions.filter((p) => p !== permission);
  const rolePermissionImplications = resource.rolePermissionImplications.filter(
    (implication) => implication.permission !== permission
  );
  const sharing = {
    ...resource.sharing,
    canSharePublic: resource.sharing.canSharePublic
      ? resource.sharing.canSharePublic.filter((p) => p !== permission)
      : null,
  };
  const newResource = {
    ...resource,
    permissions,
    rolePermissionImplications,
    sharing,
  };
  const resources = updateIdx(state.resources, resourceIndex, newResource);
  return { ...state, resources };
}

export function toggleRolePermissionImplication(
  state: State,
  resourceName: string,
  role: string,
  permission: string
): State {
  const resourceIndex = state.resources.findIndex(
    (resource) => resource.name === resourceName
  );
  const resource = state.resources[resourceIndex];
  const implication = { role, permission };
  const existingImplicationIndex =
    resource.rolePermissionImplications.findIndex((impl) =>
      _.isEqual(impl, implication)
    );
  const rolePermissionImplications =
    existingImplicationIndex === -1
      ? [...resource.rolePermissionImplications, implication]
      : deleteIdx(
          resource.rolePermissionImplications,
          existingImplicationIndex
        );
  const newResource = { ...resource, rolePermissionImplications };
  const resources = updateIdx(state.resources, resourceIndex, newResource);
  return { ...state, resources };
}

export function addPermission(
  state: State,
  resourceName: string,
  permission: string
): State {
  const resourceIndex = state.resources.findIndex(
    (resource) => resource.name === resourceName
  );
  const resource = state.resources[resourceIndex];
  const permissions = _.uniq([...resource.permissions, permission]);
  const newResource = { ...resource, permissions };
  const resources = updateIdx(state.resources, resourceIndex, newResource);
  return { ...state, resources };
}

export function updateRoleInheritanceConfiguration(
  state: State,
  resourceName: string,
  relatedResource: string,
  parentRole: string,
  thisRole: string
): State {
  const resourceIndex = state.resources.findIndex(
    (resource) => resource.name === resourceName
  );
  const resource = state.resources[resourceIndex];
  const relationIndex = resource.relations.findIndex(
    (relation) => relation.resource === relatedResource
  );
  const relation = resource.relations[relationIndex];
  if (relation.roleInheritance.kind === "simple") {
    throw new Error("this is far too simple");
  }
  const configuration = relation.roleInheritance.configuration.filter(
    (config) => config.parentRole !== parentRole
  );
  if (thisRole) {
    configuration.push({ parentRole, thisRole });
  }
  const roleInheritance = { ...relation.roleInheritance, configuration };
  const newRelation = { ...relation, roleInheritance };
  const newRelations = updateIdx(
    resource.relations,
    relationIndex,
    newRelation
  );
  const newResource = { ...resource, relations: newRelations };
  const newResources = updateIdx(state.resources, resourceIndex, newResource);
  return { ...state, resources: newResources };
}

export function toggleCanShareWithOtherUsers(
  state: State,
  resourceName: string
): State {
  const resourceIndex = state.resources.findIndex(
    (resource) => resource.name === resourceName
  );
  const resource = state.resources[resourceIndex];
  const sharing = {
    ...resource.sharing,
    canShareWithOtherUsers: resource.sharing.canShareWithOtherUsers
      ? null
      : [resource.roles[0]],
  };
  const newResource = { ...resource, sharing };
  const resources = updateIdx(state.resources, resourceIndex, newResource);
  const newState = { ...state, resources };
  return sharing.canShareWithOtherUsers && !resource.isGrantedAccessTo
    ? updateResourceIsGrantedAccessTo(newState, resourceName, true)
    : newState;
}

export function toggleCanShareWithOtherUsersRole(
  state: State,
  resourceName: string,
  role: string
) {
  const resourceIndex = state.resources.findIndex(
    (resource) => resource.name === resourceName
  );
  const resource = state.resources[resourceIndex];
  const oldCanShareWithOtherUsers = resource.sharing.canShareWithOtherUsers;
  if (oldCanShareWithOtherUsers === null) {
    throw new Error("share far more");
  }
  const canShareWithOtherUsers = oldCanShareWithOtherUsers.includes(role)
    ? oldCanShareWithOtherUsers.filter((r) => r !== role)
    : [...oldCanShareWithOtherUsers, role];
  const newResource = {
    ...resource,
    sharing: { ...resource.sharing, canShareWithOtherUsers },
  };
  const resources = updateIdx(state.resources, resourceIndex, newResource);
  return { ...state, resources };
}

export function toggleCanShareWithParent(
  state: State,
  resourceName: string,
  parentResourceName: string
): State {
  const resourceIndex = state.resources.findIndex(
    (resource) => resource.name === resourceName
  );
  const resource = state.resources[resourceIndex];
  const canShareWithParent = resource.sharing.canShareWithParent.find(
    (sharing) => sharing.parent === parentResourceName
  )
    ? resource.sharing.canShareWithParent.filter(
        (sharing) => sharing.parent !== parentResourceName
      )
    : [
        ...resource.sharing.canShareWithParent,
        {
          parent: parentResourceName,
          configuration: defaultRoleInheritanceConfiguration(
            state,
            resourceName,
            parentResourceName
          ),
        },
      ];
  const sharing: Sharing = {
    ...resource.sharing,
    canShareWithParent,
  };
  const newResource = { ...resource, sharing };
  const resources = updateIdx(state.resources, resourceIndex, newResource);
  return { ...state, resources };
}

export function updateCanShareWithParentInheritance(
  state: State,
  resourceName: string,
  parentName: string,
  parentRole: string,
  thisRole: string
): State {
  const resourceIndex = state.resources.findIndex(
    (resource) => resource.name === resourceName
  );
  const resource = state.resources[resourceIndex];
  const shareWithParentIndex = resource.sharing.canShareWithParent.findIndex(
    (shareWithParent) => shareWithParent.parent === parentName
  );
  const shareWithParent =
    resource.sharing.canShareWithParent[shareWithParentIndex];
  const configuration = shareWithParent.configuration.filter(
    (config) => config.parentRole !== parentRole
  );
  if (thisRole) {
    configuration.push({ parentRole, thisRole });
  }
  const newShareWithParent = { ...shareWithParent, configuration };
  const canShareWithParent = updateIdx(
    resource.sharing.canShareWithParent,
    shareWithParentIndex,
    newShareWithParent
  );
  const sharing = { ...resource.sharing, canShareWithParent };
  const newResource = { ...resource, sharing };
  const resources = updateIdx(state.resources, resourceIndex, newResource);
  return { ...state, resources };
}

export function toggleCanSharePublic(
  state: State,
  resourceName: string
): State {
  const resourceIndex = state.resources.findIndex(
    (resource) => resource.name === resourceName
  );
  const resource = state.resources[resourceIndex];
  const sharing: Sharing = {
    ...resource.sharing,
    canSharePublic: resource.sharing.canSharePublic
      ? null
      : [resource.permissions[0]],
  };
  const newResource = { ...resource, sharing };
  const resources = updateIdx(state.resources, resourceIndex, newResource);
  return { ...state, resources };
}

export function toggleCanSharePublicPermission(
  state: State,
  resourceName: string,
  permission: string
): State {
  const resourceIndex = state.resources.findIndex(
    (resource) => resource.name === resourceName
  );
  const resource = state.resources[resourceIndex];
  const oldCanSharePublic = resource.sharing.canSharePublic;
  if (oldCanSharePublic === null) {
    throw new Error("share far more");
  }
  const canSharePublic = oldCanSharePublic.includes(permission)
    ? oldCanSharePublic.filter((p) => p !== permission)
    : [...oldCanSharePublic, permission];
  const newResource = {
    ...resource,
    sharing: { ...resource.sharing, canSharePublic },
  };
  const resources = updateIdx(state.resources, resourceIndex, newResource);
  return { ...state, resources };
}

type Example = {
  name: string;
  resources: Resource[];
};

export const EXAMPLES: Example[] = [
  {
    name: "GitHub",
    resources: [
      {
        name: "Organization",
        isGrantedAccessTo: true,
        relations: [],
        roles: ["member", "admin"],
        permissions: ["view", "rename", "delete", "invite"],
        rolePermissionImplications: [
          {
            role: "member",
            permission: "view",
          },
          {
            role: "admin",
            permission: "view",
          },
          {
            role: "admin",
            permission: "rename",
          },
          {
            role: "admin",
            permission: "delete",
          },
          {
            role: "admin",
            permission: "invite",
          },
        ],
        sharing: {
          canShareWithOtherUsers: null,
          canShareWithParent: [],
          canSharePublic: null,
        },
      },
      {
        name: "Repository",
        isGrantedAccessTo: false,
        relations: [
          { resource: "Organization", roleInheritance: { kind: "simple" } },
        ],
        roles: ["member", "admin"],
        permissions: ["delete", "push", "pull"],
        rolePermissionImplications: [
          {
            role: "member",
            permission: "pull",
          },
          {
            role: "admin",
            permission: "pull",
          },
          {
            role: "admin",
            permission: "push",
          },
          {
            role: "admin",
            permission: "delete",
          },
        ],
        sharing: {
          canShareWithOtherUsers: null,
          canShareWithParent: [],
          canSharePublic: ["pull"],
        },
      },
    ],
  },
];

// TODO migration support? for now bail on older version
export const CURRENT_STATE_VERSION = "0.2.0";

export const encodeState = (state: StateWithMetadata): string => {
  return encodeURIComponent(JSON.stringify(state));
};

export const decodeState = (encodedState: string): StateWithMetadata | null => {
  try {
    // TODO probably need some validation here?
    const stateWithMetadata: Partial<StateWithMetadata> = JSON.parse(
      decodeURIComponent(encodedState)
    );
    return {
      sharedBy: stateWithMetadata.sharedBy || [],
      stateVersion: stateWithMetadata.stateVersion || CURRENT_STATE_VERSION,
      state: stateWithMetadata.state || { resources: [] },
    };
  } catch (e) {
    console.error("Error decoding state:", e);
    // TODO log to Sentry?
    return null;
  }
};
